import { useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@mustauto/ui/web';
import { formatApiError } from '@mustauto/utils';
import {
  QUOTE_RESPONSE_CHANNEL_LABELS,
  WORKSHOP_QUOTE_RESPONSE_CHANNELS,
  type WorkshopQuoteResponseChannel,
} from '@mustauto/types/quote-response';
import { api } from '@/lib/api';
import { formatReais } from '@/lib/utils';

type Answer = 'approved' | 'rejected';

/**
 * "Cliente aprovou" / "Cliente recusou" no orçamento pendente.
 *
 * O cliente nem sempre baixa o app para aprovar: responde no WhatsApp, liga
 * ou fala no balcão. Aqui o gestor registra essa resposta, dizendo por onde
 * ela chegou — com o nome dele, fica no orçamento e no histórico da OS, que é
 * o que a oficina tem para mostrar numa discussão. Mesma rota do app
 * (`PATCH /quotes/:id`); o efeito é o da aprovação pelo app do cliente.
 */
export function QuoteResponseButtons({
  osId,
  quoteId,
  total,
}: {
  osId: string;
  quoteId: string;
  /** `quotes.total` cru — numeric(12,2), REAIS. */
  total: string;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [channel, setChannel] = useState<WorkshopQuoteResponseChannel | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const approving = answer === 'approved';
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['os-quotes', osId] }),
      queryClient.invalidateQueries({ queryKey: ['audit-log', osId] }),
    ]);

  const open = (a: Answer) => {
    setAnswer(a);
    setChannel(null);
    setError('');
  };
  const close = () => {
    if (saving) return;
    setAnswer(null);
  };

  const confirm = async () => {
    if (!answer || !channel) return;
    setSaving(true);
    setError('');
    try {
      await api.patch(`/quotes/${quoteId}`, { status: answer, channel });
      await refresh();
      toast({
        type: 'success',
        title: approving ? 'Aprovação registrada' : 'Recusa registrada',
        description: approving
          ? 'Vale como a aprovação pelo app do cliente. Extras depois disso vão em orçamento adicional.'
          : 'Ajuste os itens e gere um orçamento novo quando o cliente quiser.',
      });
      setAnswer(null);
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 409) {
        // Respondido por outro caminho (o cliente pelo app, alguém da equipe)
        // ou vencido enquanto o diálogo estava aberto: a lista recarrega.
        await refresh();
        setAnswer(null);
        toast({
          type: 'warning',
          title: 'Orçamento não está mais pendente',
          description: 'Ele já foi respondido ou passou da validade. A lista foi atualizada.',
        });
      } else {
        setError(formatApiError(err, 'Não foi possível registrar a resposta. Tente novamente.'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex gap-2">
        <button
          type="button"
          className="flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-semibold text-primary-foreground hover:opacity-90"
          onClick={() => open('approved')}
        >
          <Check size={14} />
          Cliente aprovou
        </button>
        <button
          type="button"
          className="flex h-8 items-center gap-1.5 rounded-md border border-input px-3 text-sm font-medium hover:bg-muted"
          onClick={() => open('rejected')}
        >
          <X size={14} />
          Cliente recusou
        </button>
      </div>

      {answer && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center"
          onClick={close}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="quote-response-title"
            className="w-full max-w-md rounded-t-xl bg-background p-6 shadow-xl sm:rounded-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="quote-response-title" className="text-lg font-semibold">
              {approving ? 'Cliente aprovou o orçamento' : 'Cliente recusou o orçamento'}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Orçamento de <strong className="text-foreground">{formatReais(Number(total))}</strong>.{' '}
              {approving
                ? 'Vale como a aprovação pelo app do cliente: no app do mecânico os itens ficam travados.'
                : 'Depois é só ajustar os itens e gerar outro.'}
            </p>

            <fieldset className="mt-4">
              <legend className="text-sm font-medium">Por onde ele respondeu?</legend>
              <div className="mt-2 flex flex-wrap gap-2">
                {WORKSHOP_QUOTE_RESPONSE_CHANNELS.map((c) => (
                  <label
                    key={c}
                    className={`flex cursor-pointer items-center rounded-md border px-3 py-2 text-sm has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary ${
                      channel === c ? 'border-primary bg-primary/10 font-medium' : 'border-input hover:bg-muted'
                    }`}
                  >
                    <input
                      type="radio"
                      name="quote-response-channel"
                      value={c}
                      checked={channel === c}
                      onChange={() => setChannel(c)}
                      disabled={saving}
                      className="sr-only"
                    />
                    {QUOTE_RESPONSE_CHANNEL_LABELS[c]}
                  </label>
                ))}
              </div>
            </fieldset>
            <p className="mt-2 text-xs text-muted-foreground">
              Fica registrado no orçamento e no histórico da OS, com o seu nome.
            </p>

            {error && (
              <p role="alert" className="mt-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
                onClick={close}
                disabled={saving}
              >
                Voltar
              </button>
              <button
                type="button"
                className={`flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold transition-opacity disabled:opacity-50 ${
                  approving ? 'bg-primary text-primary-foreground' : 'bg-destructive text-destructive-foreground'
                }`}
                disabled={!channel || saving}
                onClick={() => void confirm()}
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                {approving ? 'Registrar aprovação' : 'Registrar recusa'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
